import { FC, useState } from 'react';
import { fabric } from 'fabric';
import styled from 'styled-components';
import {
  useEditorData,
  useEditorHistory,
  useFabricEditor,
} from '../../lib/FabricJs';
import { useHandleHistory } from '../../lib/FabricJs/hooks/useHandleHistory';
import { SketchPicker } from 'react-color';
import Popover from '@mui/material/Popover';
import { Plus } from 'react-feather';
import {
  CacheEnum,
  setLastUsedValue,
} from '../../lib/FabricJs/utils/sessionCache';
import { colors, defaultColorSet } from '../../configs';
import { handleWorldClockColorChange } from './InfoWidgets/WorldClockWidget';
import ElementPicker from '../Toolbar/ElementPicker';
import { handleDateColorChange } from './InfoWidgets/DateWidget';
import { IFRAME_WIDGETS } from '../../config';
import { SelectMode } from '../../types/types';
import { editorTheme } from '../../theme';
import { isTextObject } from '../Toolbar/useSelectedText';
import { collectSlideColors, ColorParser } from '../../utils/slideColors';

const { WORLD_CLOCK, DATE } = IFRAME_WIDGETS;

interface StyleProps {
  color: string;
}

const SwatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
`;

const Swatch = styled.button<StyleProps>`
  aspect-ratio: 1;
  width: 100%;
  border-radius: 6px;
  border: 1px solid ${editorTheme.border};
  background-color: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover {
    border-color: ${editorTheme.accent};
  }
`;

const CustomSwatch = styled(Swatch)`
  border: 2px solid transparent;
  background: linear-gradient(white, white) padding-box,
    linear-gradient(135deg, #ff3131, #5e17eb, #00bf63, #0097b2, #ff914d)
      border-box;
`;

const CurrentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: ${editorTheme.textMuted};
  & > * {
    width: 32px;
    flex: none;
  }
`;

const SubLabel = styled.span`
  font-size: 12px;
  color: ${editorTheme.textMuted};
`;

// Fabric's own parsers, which (unlike new fabric.Color) don't turn values
// they can't read into black. The name map is missing from the typings.
const parseColor: ColorParser = (value) => {
  const named = (fabric.Color as any).colorNameMap?.[value];
  return (
    fabric.Color.sourceFromHex(named || value) ||
    fabric.Color.sourceFromRgb(value) ||
    fabric.Color.sourceFromHsl(value)
  );
};

const getColorValues = (canvas: fabric.Canvas) => {
  const values: unknown[] = [canvas.backgroundColor];
  const visit = (object: fabric.Object) => {
    if (object.type === 'group') {
      (object as fabric.Group).getObjects().forEach(visit);
      return;
    }
    // Images and videos have a default fill that is never shown.
    if (object.type === 'image') return;
    values.push(object.fill, object.backgroundColor, object.data?.color);
  };
  canvas.getObjects().forEach(visit);
  return values;
};

interface ColorPickerProps {
  // Color only the selected text (the text toolbar's color button), not the
  // shapes or images selected with it.
  textOnly?: boolean;
}

const ColorPicker: FC<ColorPickerProps> = ({ textOnly }) => {
  const { activeColor, setActiveColor, setHistory, element } = useEditorData();
  const { handleHistory } = useHandleHistory();
  const { history } = useEditorHistory();
  const { canvas } = useFabricEditor();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColor = (color: string) => {
    const selected = canvas?.getActiveObjects();
    const activeObjects = textOnly ? selected?.filter(isTextObject) : selected;
    setActiveColor(color);
    setLastUsedValue(CacheEnum.Color, activeObjects);
    if (activeObjects?.length) {
      activeObjects.map((object) => {
        if (object.type === SelectMode.GROUP) {
          // @ts-ignore
          object.getObjects().forEach((item) => {
            item.set({ fill: color });
          });
        }
        switch (object?.data?.type) {
          case WORLD_CLOCK:
            if (element === 'digits') {
              handleWorldClockColorChange(object, color, canvas);
              object.set('data', { ...object.data, color: color });
              return;
            }
            break;
          case DATE:
            if (element === 'date') {
              handleDateColorChange(object, color, canvas);
              object.set('data', { ...object.data, color: color });
              return;
            }
            break;
        }

        object.set({ fill: color });
      });
      handleHistory(canvas ?? undefined);
    } else {
      canvas?.setBackgroundColor(color, () => {});
      if (
        // @ts-ignore;
        !history.past.find((object) => object?.backgroundColor === colors.white)
      ) {
        setHistory({ backgroundColor: colors.white });
      }
      setHistory({ backgroundColor: color });
    }
    canvas?.requestRenderAll();
  };

  const slideColors = canvas
    ? collectSlideColors(getColorValues(canvas), parseColor)
    : [];

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <>
      {!textOnly && <ElementPicker />}
      <CurrentRow>
        <CustomSwatch
          color="white"
          aria-describedby={id}
          aria-label="Pick a custom color"
          onClick={handleClick}
        >
          <Plus size={16} />
        </CustomSwatch>
        <Swatch as="span" color={activeColor} aria-hidden="true" />
        <span>{String(activeColor).toUpperCase()}</span>
      </CurrentRow>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <SketchPicker
          color={activeColor}
          onChange={(data) => {
            handleColor(data?.hex);
          }}
          disableAlpha
        />
      </Popover>
      {slideColors.length > 0 && (
        <>
          <SubLabel>On this slide</SubLabel>
          <SwatchGrid>
            {slideColors.map((color) => (
              <Swatch
                key={color}
                color={color}
                aria-label={`Use color ${color}, used on this slide`}
                onClick={() => handleColor(color)}
              />
            ))}
          </SwatchGrid>
        </>
      )}
      <SubLabel>Presets</SubLabel>
      <SwatchGrid>
        {defaultColorSet.map((color, key) => (
          <Swatch
            key={key}
            color={color}
            aria-label={`Use color ${color}`}
            onClick={() => handleColor(color)}
          />
        ))}
      </SwatchGrid>
    </>
  );
};

export default ColorPicker;
